import React from 'react';
import isEmpty from 'lodash/isEmpty';
import styled, { css, useTheme } from 'styled-components';
import { useSegment } from '@sparknz/journeys-common';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionItemBodyWithAnimation,
  AccordionItemHeading,
  BaseProductCard,
  Box,
  Clickable,
  Divider,
  Icon,
  ProductCardFeature,
  ProductCardPrice,
  ProductCardPromotion,
  ProductCardSection,
  Text,
  useMediaQuery,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { ConfigurablePlanCards } from 'components/shop/shared/typings/plans';
import { ActiveCategories } from '../../typings/category';
import { PlanDetails } from '../../typings/plans';
import { getCardAltText, getCardImageUrl } from '../../utils/getImage.utils';
import { getPrice } from '../../utils/getPrice.utils';
import {
  getMarketingTag,
  getPromoOffer,
  getSortedPromoCharacteristics,
} from '../../utils/getPromoDetails';
import { removePrices } from '../../utils/removePrices.utils';
import { PlanCardImpressionRegion } from './components';

interface PlanCardContainerProps {
  hasMarketingTag: boolean;
}

const FullHeightLinkRegion = styled(LinkRegion)`
  height: 100%;
`;

const StyledProductCardPrice = styled(ProductCardPrice)`
  ${({ theme: { spacing } }) => css`
    margin-bottom: ${spacing.s5};
  `}
`;

const KeyConditionsText = styled(Text)`
  ${({ theme: { spacing } }) => css`
    margin-right: ${spacing.s1};
  `}
`;

const ProductCardEntitlementSection = styled(ProductCardSection)`
  ${({ theme: { spacing, media } }) => css`
    display: flex;
    flex-direction: column;
    padding-bottom: ${spacing.s2};
    flex-grow: 1;

    ${media.up('slim')} {
      flex-grow: 0;
    }
  `}
`;

const ProductCardPromotionSection = styled(ProductCardSection)`
  ${({ theme: { spacing, media } }) => css`
    display: flex;
    flex-direction: column;
    flex-grow: 0;
    padding-bottom: ${spacing.s2};

    ${media.up('slim')} {
      flex-grow: 1;
    }
  `}
`;

const StyledProductCardFeature = styled(ProductCardFeature)`
  ${({ theme: { spacing } }) => css`
    margin-bottom: ${spacing.s2};
  `}
`;

const PlanCardContainer = styled(BaseProductCard)<PlanCardContainerProps>`
  ${({ theme: { media, spacing }, hasMarketingTag }) => css`
    display: flex;
    padding: 0;
    min-width: 260px;
    padding-top: ${spacing.s5};

    ${media.up('slim')} {
      padding-top: ${hasMarketingTag ? spacing.s6 : spacing.s2};
    }

    ${media.up('big')} {
      min-width: 310px;
    }
  `}
`;

const PlanSelectSection = styled.div`
  margin-top: auto;
`;

const PlanImage = styled.img`
  ${({ theme: { spacing } }) => css`
    margin-bottom: ${spacing.s3};
    height: 80px;
    width: 80px;
  `}
`;

export interface PlanCardProps extends Pick<ConfigurablePlanCards, 'variant'> {
  children?: React.ReactNode;
  planCardProduct: PlanDetails;
  isAccordionOpen: boolean;
  hasMarketingTag: boolean;
  activeCategories: ActiveCategories;
  handleInfoLayerChange: (contentKey: string) => void;
  setIsAccordionOpen: (prev: (value: boolean) => boolean) => void;
  renderCtaSection: ({
    planCardProduct,
  }: {
    planCardProduct: PlanDetails;
  }) => React.ReactNode;
}

const getAccordionTitle = (isExpanded: boolean) =>
  isExpanded ? 'Hide benefits' : 'See more benefits';

const PlanCard = ({
  variant = 'standard',
  planCardProduct,
  isAccordionOpen,
  hasMarketingTag,
  activeCategories,
  setIsAccordionOpen,
  handleInfoLayerChange,
  renderCtaSection,
}: PlanCardProps) => {
  const {
    palette: { baseColors },
  } = useTheme();
  const { isBusiness } = useSegment();
  const isMobileView = useMediaQuery({ max: 'lil' });

  const {
    planDetails: { planId, planName, description: blurb, contentKey },
    price,
    entitlements,
    planImages,
    promotions,
  } = planCardProduct;

  const isStandard = variant === 'standard';
  const promotionVariant = isStandard ? 'text' : 'banner';
  const images = planImages || [];

  const marketingTag = getMarketingTag(promotions);
  const promoOffer = getPromoOffer(promotions);
  const promoCharacteristics = getSortedPromoCharacteristics(promotions);
  const planCardUrl = getCardImageUrl(images);
  const planCardAltText = getCardAltText(images);
  const accordionTitle = getAccordionTitle(isAccordionOpen);

  const pricePeriod =
    Number(price?.priceFrequency) > 1
      ? `${price.priceFrequency} ${price.pricePeriod?.toLowerCase()}s`
      : price.pricePeriod?.toLowerCase();

  const handleFeatureInfoLayerChange = (featureContentKey: string) => {
    return () => {
      trackCustomLink(`View ${featureContentKey} Info Layer`);
      handleInfoLayerChange(featureContentKey);
    };
  };

  const handleAccordionChange = () => {
    trackCustomLink(!isAccordionOpen ? 'Expand benefits' : 'Collapse benefits');
    setIsAccordionOpen((prev: boolean) => !prev);
  };

  const productCardFeatures = entitlements?.length
    ? [...entitlements]
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
        .map((planFeature) => {
          const handleInfoLayer = planFeature.contentKey
            ? handleFeatureInfoLayerChange(planFeature.contentKey)
            : undefined;
          return (
            <StyledProductCardFeature
              iconUrl={planFeature.iconUrl ?? undefined}
              title={planFeature.name}
              description={planFeature.longDescription || ''}
              key={planFeature.name}
              handleInfoIconClick={handleInfoLayer}
            />
          );
        })
    : null;

  return (
    <PlanCardImpressionRegion
      plan={planCardProduct}
      activeCategories={activeCategories}
      key={planId}
    >
      <FullHeightLinkRegion regionId={`planCard.${planName}`}>
        <PlanCardContainer
          dynamicTargetValue={planId}
          hasMarketingTag={hasMarketingTag}
          marketingMessage={marketingTag?.name}
        >
          <ProductCardEntitlementSection>
            {planCardUrl ? (
              <PlanImage src={planCardUrl} alt={planCardAltText} />
            ) : null}
            <Text mb="s1" variant="subheadingM">
              {removePrices(planName)}
            </Text>
            <StyledProductCardPrice
              price={{
                priceValue: getPrice(price, isBusiness) as number,
                pricePeriod: pricePeriod || '',
                isGstExcl: isBusiness,
              }}
            />
            {promoOffer?.name ? (
              <Box pt="s2">
                <ProductCardPromotion
                  variant={promotionVariant}
                  title={promoOffer.name}
                  description={promoOffer.longDescription}
                />
              </Box>
            ) : null}
            {blurb ? (
              <Box pt="s2">
                <Text variant="bodySDemi">{blurb}</Text>
              </Box>
            ) : null}
            <Box pt="s3">{productCardFeatures}</Box>
          </ProductCardEntitlementSection>
          {!isEmpty(promoCharacteristics) ? <Divider /> : null}
          {!isEmpty(promoCharacteristics) && isMobileView ? (
            <Accordion
              allowMultipleExpanded={false}
              onChange={handleAccordionChange}
            >
              <AccordionItem
                key={planId}
                uuid={planId}
                isOpen={isAccordionOpen}
                disableBorder
              >
                <Box mx="s4">
                  <AccordionItemHeading>
                    {accordionTitle}
                    <AccordionButton />
                  </AccordionItemHeading>
                  <AccordionItemBodyWithAnimation isOpen={isAccordionOpen}>
                    {promoCharacteristics?.map((promoDetail) => {
                      return (
                        <StyledProductCardFeature
                          iconUrl={promoDetail.iconUrl as string}
                          title={promoDetail.name}
                          description={promoDetail.longDescription || ''}
                          fillColor={baseColors.kiwi}
                          key={promoDetail.name}
                          handleInfoIconClick={
                            promoDetail.contentKey
                              ? handleFeatureInfoLayerChange(
                                  promoDetail.contentKey,
                                )
                              : undefined
                          }
                        />
                      );
                    })}
                  </AccordionItemBodyWithAnimation>
                </Box>
              </AccordionItem>
            </Accordion>
          ) : null}
          {!isEmpty(promoCharacteristics) && !isMobileView ? (
            <ProductCardPromotionSection>
              {promoCharacteristics?.map((promoDetail) => {
                return (
                  <StyledProductCardFeature
                    iconUrl={promoDetail.iconUrl as string}
                    title={promoDetail.name}
                    description={promoDetail.longDescription || ''}
                    fillColor={baseColors.kiwi}
                    key={promoDetail.name}
                    handleInfoIconClick={
                      promoDetail.contentKey
                        ? handleFeatureInfoLayerChange(promoDetail.contentKey)
                        : undefined
                    }
                  />
                );
              })}
            </ProductCardPromotionSection>
          ) : null}
          <PlanSelectSection>
            <Divider />
            <Box flexDirection="row" pt="s2" mb="s2" mx="s4">
              <KeyConditionsText variant="bodySDemi">
                *View key conditions
              </KeyConditionsText>
              <Clickable
                title="View key conditions information icon"
                onClick={
                  contentKey
                    ? handleFeatureInfoLayerChange(contentKey)
                    : undefined
                }
                hasRoundedFocus
              >
                <Icon name="info_small" fill={baseColors.lupin} />
              </Clickable>
            </Box>
            <Divider />
            {renderCtaSection({ planCardProduct })}
          </PlanSelectSection>
        </PlanCardContainer>
      </FullHeightLinkRegion>
    </PlanCardImpressionRegion>
  );
};

export default PlanCard;
